/**
 * Dual Attribution Layout
 * Left: Strategy attribution (Asset Allocation, Security Selection, Currency)
 * Right: Top contributing positions (winners and losers)
 * Serves AM (position-level detail) and IA (strategy effectiveness)
 */

import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface AttributionItem {
  name: string;
  contribution: number;
}

interface DualAttributionLayoutProps {
  strategyAttribution: AttributionItem[];
  topContributors: AttributionItem[];
  bottomContributors: AttributionItem[];
}

const DualAttributionLayout: React.FC<DualAttributionLayoutProps> = ({
  strategyAttribution,
  topContributors,
  bottomContributors
}) => {
  const formatPercent = (value: number) => {
    // TASK-872: no `+` on a zero. A plus sign is a direction, and 0.0% has none — it was
    // rendering "+0.0%" on all 69 positions, in green, as though something had gone right.
    //
    // The sign is decided on the ROUNDED value, not the raw one. Review: the backend rounds
    // contributions to 2dp and this prints 1dp, so 0.04 displayed as "+0.0%" and −0.04 as
    // "-0.0%" — a sign on a printed zero, which is the thing being removed.
    const shown = Number(value.toFixed(1));
    const sign = shown > 0 ? '+' : '';
    return `${sign}${shown.toFixed(1)}%`;
  };

  const getBarWidth = (value: number, maxValue: number) => {
    return Math.abs(value / maxValue) * 100;
  };

  const nonZeroAttribution = strategyAttribution.filter(s => s.contribution !== 0);
  const maxStrategyValue = nonZeroAttribution.length > 0
    ? Math.max(...nonZeroAttribution.map(s => Math.abs(s.contribution)))
    : 1;

  // TASK-872: the same rule the LEFT panel has always applied, finally applied to the right.
  //
  // "By Strategy" says "No attribution data available" when every value is zero. Top
  // Contributors did not, so it rendered a ranked leaderboard of +0.0% under green up-arrows
  // and a Detractors list showing "+0.0%" under a RED DOWN-ARROW — the arrow, the colour and
  // the ordinal each making a claim the number does not support, twenty pixels from a panel
  // admitting there is no data.
  //
  // A rank is a claim that one thing beat another. Over a list of zeros there is nothing to
  // rank, and the order is whatever the array happened to be in.
  const hasContribution = (items: AttributionItem[]) =>
    items.some(item => item.contribution !== 0);
  const rankableTop = hasContribution(topContributors);
  const rankableBottom = hasContribution(bottomContributors);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Strategy Attribution */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">By Strategy</h4>
        {nonZeroAttribution.length === 0 ? (
          <p className="text-sm text-gray-400">No attribution data available</p>
        ) : (
        <div className="space-y-3">
          {strategyAttribution.map((item, index) => (
            <div key={index}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-700">{item.name}</span>
                <span className={`text-sm font-semibold ${
                  item.contribution >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatPercent(item.contribution)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    item.contribution >= 0 ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${getBarWidth(item.contribution, maxStrategyValue)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Right: Top Contributors */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="contributors">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Top Contributors</h4>

        {!rankableTop && !rankableBottom ? (
          <p className="text-sm text-gray-400" data-testid="contributors-empty">
            No position-level contribution data available — nothing to rank.
          </p>
        ) : (
          <>
            {/* Top 3 Winners */}
            {rankableTop && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Best Performers
                </div>
                <div className="space-y-2">
                  {topContributors.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 w-4">
                          {index + 1}.
                        </span>
                        <span className="text-sm text-gray-800">{item.name}</span>
                      </div>
                      {/* Review: the DETRACTOR arrow was made conditional and this one was
                          not, so a rank-2 zero beside one real contributor still got a green
                          up-arrow. `[2.4, 0, 0]` is the ordinary shape when a single position
                          moved — the backend pads the list to three — so this was the common
                          case, not the edge one. */}
                      <div
                        className={`flex items-center gap-1 ${
                          item.contribution > 0 ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        {item.contribution > 0 && <TrendingUp className="w-4 h-4" />}
                        <span className="text-sm font-semibold">
                          {formatPercent(item.contribution)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom Performers */}
            {rankableBottom && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase mb-2">Detractors</div>
                <div className="space-y-2">
                  {bottomContributors.slice(0, 2).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 w-4">
                          {index + 1}.
                        </span>
                        <span className="text-sm text-gray-800">{item.name}</span>
                      </div>
                      {/* The arrow follows the number, not the list it is in. A detractor at
                          exactly 0.0% detracted from nothing. */}
                      <div
                        className={`flex items-center gap-1 ${
                          item.contribution < 0 ? 'text-red-600' : 'text-gray-500'
                        }`}
                      >
                        {item.contribution < 0 && <TrendingDown className="w-4 h-4" />}
                        <span className="text-sm font-semibold">
                          {formatPercent(item.contribution)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DualAttributionLayout;
